import axios from 'axios';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { ThemeProvider } from 'styled-components';

import { fetchBess, me } from './ems-core/scripts/api';
import { Bess, SafeUser, userRoles } from './ems-core/scripts/api/types';
import { routeToOwnArea } from './ems-core/scripts/common/routing';
import { useLocalStorage } from './ems-core/scripts/hooks/localStorageHook';
import defaultTheme from './ems-core/theme';
import LoadingPage from './pages/LoadingPage';
import SigninPage, { Feedback } from './pages/SigninPage';
import BessSelectionPage from './pages/BessSelectionPage';
import RecoveryPasswordPage from './pages/RecoveryPasswordPage';

const DevArea = lazy(() => import('./DevArea'));
const OperatorArea = lazy(() => import('./OperatorArea'));
const ClientArea = lazy(() => import('./ClientArea'));

const API_BASE_URL = import.meta.env.VITE_API_URI;

const API = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const App = () => {
  const [feedback, setFeedback] = useState<Feedback>(undefined);
  const [token, setToken] = useLocalStorage<string>('token', '');
  const [bess, setBess] = useLocalStorage<string>('bess', '');
  const [features, setFeatures] = useLocalStorage<Array<string>>('features', []);
  const [bessName, setBessName] = useLocalStorage<string>('bessName', '');
  const [bessHasMeters] = useLocalStorage<boolean>('bessHasMeters', false);
  const [user, setUser] = useState<SafeUser | undefined>(undefined);
  const [userController, setUserController] = useState<boolean>(false);

  return (
    <ThemeProvider theme={defaultTheme}>
      <BrowserRouter>
        <AppContent
          feedback={feedback}
          setFeedback={setFeedback}
          token={token}
          setToken={setToken}
          bess={bess}
          setBess={setBess}
          features={features}
          setFeatures={setFeatures}
          bessName={bessName}
          setBessName={setBessName}
          bessHasMeters={bessHasMeters}
          user={user}
          setUser={setUser}
          userController={userController}
          setUserController={setUserController}
          API={API}
        />
      </BrowserRouter>
    </ThemeProvider>
  );
};

const AppContent = ({
  feedback,
  setFeedback,
  token,
  setToken,
  bess,
  setBess,
  features,
  setFeatures,
  bessName,
  setBessName,
  bessHasMeters,
  user,
  setUser,
  userController,
  setUserController,
  API,
}: any) => {
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const recoveryEmail = searchParams.get('email') ?? '';
  const recoveryToken = searchParams.get('token') ?? '';

  const onLogout = useCallback(
    (error?: string) => {
      if (error === 'noSocket') setFeedback('noSocket');
      else setFeedback('loggedOut');
      setUser(undefined);
      setBess('');
      setToken('');
      navigate('/');
      window.location.reload();
    },
    [navigate, setFeedback, setUser, setBess, setToken]
  );

  const onDeny = useCallback(() => {
    setFeedback('unauthorized');
    setUser(undefined);
    setBess('');
    setToken('');
    navigate('/');
    window.location.reload();
  }, [navigate, setFeedback, setUser, setBess, setToken]);

  // If we have a new token, update our user and our headers
  useEffect(() => {
    if (token.length > 0) {
      API.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setFeedback('loggedIn');
    }
  }, [token, setFeedback, API]);

  useEffect(() => {
    if (user === undefined && token.length > 0) {
      API.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      me(API).then((data) => {
        setUser(data);
      });
    }
  }, [token, user, setUser, API]);

  // Once we have a user, but not a bess, route to bess selection page
  useEffect(() => {
    if (user?._id == import.meta.env.VITE_ID_CLIENT_STATIC_CPFL && import.meta.env.VITE_IS_LOCAL == 'true') {
      setBess(import.meta.env.VITE_IS_BESS_CPFL ?? '');
      navigate('/client/station');
    } else if (user !== undefined && bess.length === 0) {
      navigate('/select');
    }
  }, [bess, setBess, user, navigate]);

  useEffect(() => {
    const currentPath = location.pathname;
    if (currentPath.startsWith('/recover')) return;

    const isInArea =
      currentPath.startsWith('/dev') ||
      currentPath.startsWith('/operator') ||
      currentPath.startsWith('/client') ||
      currentPath.startsWith('/select');

    if (!isInArea || currentPath === '/dev' || currentPath === '/operator' || currentPath === '/client') {
      try {
        routeToOwnArea(user, bess, navigate);
      } catch (err) {
        onDeny();
      }
    }
  }, [bess, onDeny, user, navigate, location.pathname]);

  useEffect(() => {
    if (user != undefined) {
      fetchBess(API, bess).then((bessData) => {
        if (bessData?.features) setFeatures(bessData.features);
        else setFeatures([]);

        if (bessData?.name) setBessName(bessData.name);
        else setBessName('');
      });
    }
  }, [bess, user, API]);

  const filterBess = useCallback(
    (bess: Bess) => {
      if (user) {
        return user.permissions.some(
          (permission) =>
            permission.bess === bess._id ||
            (permission.code !== userRoles.manager && permission.code <= userRoles.operator)
        );
      }
      return false;
    },
    [user]
  );

  useEffect(() => {
    const checkAuth = async () => {
      const url = location.pathname;
      let permissions = user?.permissions;

      if (user === undefined && token.length > 0) {
        API.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const meUser = await me(API);
        setUser(meUser);
        permissions = meUser.permissions;
      }

      const isDevRoute = url.startsWith('/dev');
      const isClientRoute = url.startsWith('/client');
      const isOperatorRoute = url.startsWith('/operator');
      const isBessSelectRoute = url === '/select';
      const isRestricted = isDevRoute || isClientRoute || isOperatorRoute;

      // User is attempting to access select area without logging in
      if (isBessSelectRoute && (token.length === 0 || !user)) {
        onDeny();
        return;
      }

      if (url.match('/control/') && !userController) {
        onDeny();
        return;
      }

      // All permissions to the current bess being accessed
      const permissionsToThisbess = permissions?.find(
        (permission: any) => permission.bess === bess || permission.code <= userRoles.operator
      );

      // User is attempting to access a restrict area
      if (isRestricted) {
        if (!permissions) {
          onDeny();
          return;
        }

        if (bess.length === 0 || !permissionsToThisbess) {
          setBess('');
          navigate('/select');
          return;
        }

        if (
          isClientRoute &&
          !permissions?.find(({ code }: any) => code === userRoles.client || code <= userRoles.mouraDev)
        ) {
          onDeny();
          return;
        }

        if (isOperatorRoute && !permissions?.find(({ code }: any) => code <= userRoles.operator)) {
          onDeny();
          return;
        }

        if (isDevRoute && !permissions?.find(({ code }: any) => code <= userRoles.manager)) {
          onDeny();
          return;
        }
      }
      // User redirect to '/', but already logged in system.
      else if (url === '/' && permissions) {
        if (bess.length === 0 || !permissionsToThisbess) {
          setBess('');
          navigate('/select');
          return;
        }

        if (permissions?.find(({ code }: any) => code <= userRoles.mouraDev || code === userRoles.manager)) {
          navigate('/dev');
          return;
        }

        if (permissions?.find(({ code }: any) => code > userRoles.mouraDev && code <= userRoles.operator)) {
          navigate('/operator');
          return;
        }

        if (permissions?.find(({ code }: any) => code === userRoles.client || code <= userRoles.mouraDev)) {
          navigate('/client');
          return;
        }
      }
    };

    checkAuth();
  }, [location.pathname, user, token, bess, userController, API, setUser, onDeny, setBess, navigate]);

  return (
    <Suspense fallback={<LoadingPage />}>
      <Routes>
        {/** SIGNIN */}
        <Route path='/' element={<SigninPage api={API} feedback={feedback} setToken={setToken} setUser={setUser} />} />

        {/** PASSWORD RECOVERY */}
        <Route
          path='/recover'
          element={<RecoveryPasswordPage api={API} email={recoveryEmail} token={recoveryToken} />}
        />

        {/** BESS SELECT */}
        <Route
          path='/select'
          element={
            <BessSelectionPage
              api={API}
              token={token}
              setBess={(selected: string) => {
                setBess(selected);
                // Navegar automaticamente após selecionar um BESS
                setTimeout(() => {
                  try {
                    routeToOwnArea(user, selected, navigate);
                  } catch (err) {
                    console.log(err);
                    onDeny();
                  }
                }, 100); // Pequeno delay para garantir que o estado seja atualizado
              }}
              onGoBack={onLogout}
              role={user?.permissions[0].code}
              filterBess={filterBess}
            />
          }
        />

        {/** DEVELOPER AREA */}
        <Route
          path='/dev/*'
          element={
            <DevArea
              token={token}
              bess={bess}
              control={userController}
              features={features}
              bessName={bessName}
              hasMeters={bessHasMeters}
              api={API}
              setController={(ctrl: boolean) => {
                setUserController(ctrl);
              }}
              username={user?.email?.split('@')[0] ?? 'Desconhecido'}
              email={user?.email}
              userid={user?._id}
              role={user?.permissions[0].code}
              onLogOut={onLogout}
            />
          }
        />

        {/** OPERATOR AREA */}
        <Route
          path='/operator/*'
          element={
            <OperatorArea
              token={token}
              bess={bess}
              features={features}
              bessName={bessName}
              control={userController}
              hasMeters={bessHasMeters}
              api={API}
              setController={(ctrl: boolean) => {
                setUserController(ctrl);
              }}
              username={user?.email?.split('@')[0] ?? 'Desconhecido'}
              role={user?.permissions[0].code}
              email={user?.email}
              userid={user?._id}
              onLogOut={onLogout}
            />
          }
        />

        {/** CLIENT AREA */}
        <Route
          path='/client/*'
          element={
            <ClientArea
              token={token}
              bess={bess}
              features={features}
              bessName={bessName}
              hasMeters={bessHasMeters}
              api={API}
              username={user?.email?.split('@')[0] ?? 'Desconhecido'}
              role={user?.permissions[0].code}
              email={user?.email}
              userid={user?._id}
              onLogOut={onLogout}
            />
          }
        />
      </Routes>
    </Suspense>
  );
};
